import { useCallback, useEffect, useState, useRef } from 'react';
import { jwtDecode } from 'jwt-decode';

import { consumeAuthState, createAuthState } from '../helpers/authState';
import { getAuthToken, setAuthToken } from '../helpers/authToken';

const TOKEN_EXPIRATION_CHECK_INTERVAL = 10000;

function getTokenExpirationTime(token: string) {
  const decoded = jwtDecode(token);

  return (decoded.exp ?? 0) * 1000;
}

function isTokenExpired(expiresAt: number | null) {
  return Boolean(expiresAt && Date.now() > expiresAt);
}

type TokenState = {
  value: string | null;
  expiresAt: number | null;
};

const useOktaAuth = function (loginUrl = '') {
  const [token, setToken] = useState<TokenState>({
    value: null,
    expiresAt: null,
  });
  const [expired, setExpired] = useState(false);
  const [authenticated, setAuthenticated] = useState(false);
  const expirationCheckInterval = useRef<ReturnType<typeof setInterval> | null>(
    null
  );

  const redirectToLoginUrl = useCallback(
    () =>
      setTimeout(() => {
        // the login endpoint must send `state` back along with the token (see the hash handling below)
        const url = new URL(loginUrl);

        url.searchParams.set('state', createAuthState());
        window.location.href = url.toString();
      }, 1000),
    [loginUrl]
  );

  // fired on component mount - check if JWT is present in the url or localStorage and check its validity
  useEffect(() => {
    const hashParams = new URLSearchParams(window.location.hash.slice(1));
    let tokenValue: string | null = null;

    if (hashParams.has('token')) {
      // a token in the url is accepted only when it comes with the `state` generated by this tab
      // before the login redirect - otherwise it's a link someone else shared and it's ignored
      if (consumeAuthState(hashParams.get('state'))) {
        tokenValue = hashParams.get('token');
      }

      // clean the hash fragment from the url (#token=*) without causing a page reload
      window.history.replaceState(null, '', ' ');
    }

    if (!tokenValue) {
      tokenValue = getAuthToken();
    }

    // redirect to login page - there's no token in the cache and in the url
    if (!tokenValue) {
      redirectToLoginUrl();
      return;
    }

    const expiresAt = getTokenExpirationTime(tokenValue);

    // redirect to login page - the token is expired when admin is entered
    if (isTokenExpired(expiresAt)) {
      setExpired(true);

      redirectToLoginUrl();
      return;
    }

    setAuthToken(tokenValue);

    setAuthenticated(true);
    setToken({ value: tokenValue, expiresAt });
  }, [redirectToLoginUrl]);

  // periodically check token for expiration
  useEffect(() => {
    if (token && !expired) {
      expirationCheckInterval.current = setInterval(() => {
        const isExpired = isTokenExpired(token.expiresAt);

        if (isExpired !== expired) {
          setExpired(isExpired);
        }
      }, TOKEN_EXPIRATION_CHECK_INTERVAL);
    } else {
      clearInterval(expirationCheckInterval.current ?? undefined);
      expirationCheckInterval.current = null;
    }

    return () => {
      clearInterval(expirationCheckInterval.current ?? undefined);
      expirationCheckInterval.current = null;
    };
  }, [expired, token]);

  return {
    authenticated,
    expired,
  };
};

export { useOktaAuth };
